<template>
  <Modal
    offset-menu
    :title="modalTitle"
  >
    <template v-if="showNoTeamsWarning">
      {{ $t("modals.push_to_team_modal.no_teams_warning") }}
    </template>
    <template v-else>
      <div>
        <i18n-t
          keypath="modals.push_to_team_modal.select_teams_or_users"
          scope="global"
        >
          <template
            v-if="mention.title"
            #mentionTitle
          >
            <strong>{{ mention.title }}</strong>
          </template>
        </i18n-t>
      </div>
      <InputSelect
        v-model="selectedOption"
        class="q-mt-lg"
        :label="$t('modals.push_to_team_modal.select_teams_or_users_label')"
        new-design
        option-label="label"
        option-value="id"
        :options="filteredDropdownOptions"
        use-input
        @filter="filterDropdownOptions"
        @update:model-value="selectOption(selectedOption)"
      >
        <template #option="{ active, disabled, selected, option }">
          <div
            class="team-list-item flex items-center"
            :class="{
              active,
              disabled,
              selected,
            }"
          >
            <div class="team-list-item-avatar">
              <img
                v-if="option.logo_url"
                :alt="$t('modals.push_to_team_modal.source_logo_image')"
                :src="option.logo_url"
              />
              <BaseIcon
                v-else
                :icon="option.icon"
              />
            </div>
            <div class="team-list-item-label">
              {{ option.label }}
            </div>
            <div
              v-if="option.type === 'team'"
              class="team-list-item-team"
            >
              (<BaseIcon icon="followers" /> {{ option.users_count }})
            </div>
          </div>
        </template>
      </InputSelect>

      <div
        v-if="selectedOptions.length"
        class="team-list q-mt-xs"
      >
        <div
          v-for="(selection, index) in selectedOptions"
          :key="selection.id"
          class="team-list-item"
          @mouseenter="hoverIndex = index"
          @mouseleave="hoverIndex = null"
        >
          <div class="team-list-item-avatar">
            <img
              v-if="selection.logo_url"
              :alt="$t('modals.push_to_team_modal.source_logo_image')"
              :src="selection.logo_url"
            />
            <BaseIcon
              v-else
              :icon="selection.icon"
            />
          </div>
          <div class="team-list-item-label">
            {{ selection.label }}
          </div>
          <div>
            <BaseButton
              v-if="hoverIndex === index"
              circle
              flat
              icon="close"
              new-design
              new-icon
              size="sm"
              @click="deleteOption(selection)"
            />
          </div>
        </div>
      </div>

      <InputText
        v-model="text"
        class="q-mt-lg"
        :label="$t('modals.push_to_team_modal.include_note_label')"
        new-design
        rows="4"
        type="textarea"
      />
    </template>
    <template #buttons>
      <BaseButton
        v-if="!$isMobile"
        color="white"
        :label="$t('global.cancel')"
        new-design
        @click.prevent="$modals.close('PushToTeamModal')"
      />
      <BaseButton
        v-if="!showNoTeamsWarning"
        color="primary"
        :disabled="!selectedOptions.length"
        :label="$t('modals.push_to_team_modal.push_button')"
        new-design
        @click="save"
      />
    </template>
  </Modal>
</template>

<script>
import { intersection } from "lodash-es";
import { storeToRefs } from "pinia";

import {
  BaseButton,
  BaseIcon,
  InputSelect,
  InputText,
} from "shared/components/base";
import Modal from "shared/components/core/modals/Modal.vue";
import useFilteredTeam from "shared/composables/useFilteredTeam";
import useProductMenu from "shared/composables/useProductMenu";
import useTeamPicker from "shared/composables/useTeamPicker";
import { useUserStore } from "shared/stores/user";

export default {
  name: "PushToTeamModal",
  components: {
    Modal,
    BaseButton,
    BaseIcon,
    InputSelect,
    InputText,
  },
  props: {
    mention: {
      type: Object,
      required: true,
    },
    archivedContentContext: {
      type: Boolean,
    },
  },
  setup() {
    const { currentRouteProductMenu } = useProductMenu();

    const userStore = useUserStore();
    const { currentUser, sortedTeam, organisation } = storeToRefs(userStore);
    const { getTeam } = userStore;

    const { teamUsers } = useFilteredTeam();
    const { selectedTeamPreference, organisationTeamOptions } = useTeamPicker();

    return {
      currentRouteProductMenu,
      currentUser,
      sortedTeam,
      organisation,
      getTeam,
      organisationTeamOptions,
      selectedTeamPreference,
      teamUsers,
    };
  },
  data() {
    return {
      selectedOption: "",
      selectedOptions: [],
      teamUserFilter: "",
      text: "",
      team: [],
      hoverIndex: null,
    };
  },
  computed: {
    showNoTeamsWarning() {
      return (
        this.restrictContentSharingToTeam &&
        this.teamOptions.length === 0 &&
        this.usersOptions.length === 0
      );
    },
    modalTitle() {
      if (this.mention.type === "external_item")
        return this.$t("modals.push_to_team_modal.external_item_title");

      return this.$t(
        `modals.push_to_team_modal.mention_title.${this.mention.type}`
      );
    },
    mentionType() {
      const typeMap = {
        radio_clip: "RadioClip",
        radio_article: "RadioArticle",
        article: "Article",
        tv_caption: "TvCaption",
        tv_super: "TvSuper",
        tv_logo_appearance: "TvLogoAppearance",
        paper_article: "PaperArticle",
        magazine_article: "MagazineArticle",
        podcast_episode: "PodcastEpisode",
        tweet: "Tweet",
        facebook_post: "FacebookPost",
        youtube_video: "YoutubeVideo",
        instagram_post: "InstagramPost",
        instagram_comment: "InstagramComment",
        reddit_post: "RedditPost",
        reddit_post_comment: "RedditPostComment",
        external_item: "ExternalItem",
        forum_post: "ForumPost",
        blog_post: "BlogPost",
        bluesky_post: "BlueskyPost",
      };

      return typeMap[this.mention.type];
    },
    getMentionText() {
      return (
        this.mention.text || this.mention.excerpt || this.mention.summary || ""
      );
    },
    restrictContentSharingToTeam() {
      return (
        (this.$features.has("has_restricted_content_sharing_to_team") &&
          !this.$permissions.has("can_share_with_all_organisation_users")) ||
        this.$features.has("has_team_workspaces")
      );
    },
    teamOptions() {
      let teams = this.organisation.organisation_teams || [];

      if (this.restrictContentSharingToTeam) {
        teams = this.currentUser.organisation_teams;
      }

      if (this.$features.has("has_team_workspaces")) {
        teams = this.organisationTeamOptions.filter(
          (team) => team.id === this.selectedTeamPreference
        );
      }

      return teams.map(this.mapTeamOption);
    },
    usersOptions() {
      if (!this.restrictContentSharingToTeam) {
        return this.team.map(this.mapUserOption);
      }

      if (this.$features.has("has_team_workspaces")) {
        return this.teamUsers.map(this.mapUserOption);
      }

      const currentUserTeamIds = this.currentUser.organisation_teams.map(
        (team) => team.id
      );

      const filteredUsers = this.team.filter((user) => {
        const userTeamIds = user.organisation_teams.map((team) => team.id);

        return intersection(userTeamIds, currentUserTeamIds).length;
      });

      return filteredUsers.map(this.mapUserOption);
    },
    dropdownOptions() {
      return [].concat(this.teamOptions, this.usersOptions);
    },
    filteredDropdownOptions() {
      if (this.teamUserFilter) {
        const filterRegexp = new RegExp(this.teamUserFilter, "i");

        return this.dropdownOptions.filter((option) =>
          filterRegexp.test(option.label)
        );
      }

      return this.dropdownOptions;
    },
  },
  watch: {
    sortedTeam: {
      immediate: true,
      deep: true,
      handler() {
        this.team = JSON.parse(JSON.stringify(this.sortedTeam));
      },
    },
  },
  async mounted() {
    if (!this.sortedTeam.length) await this.getTeam();
  },
  methods: {
    mapTeamOption(team) {
      return {
        users_count: team.users_count,
        type: "team",
        label: team.name,
        id: team.id,
        logo_url: team.logo_url,
        icon: "followers",
        disabled: false,
      };
    },
    mapUserOption(user) {
      return {
        ...user,
        type: "user",
        label: `${user.first_name} ${user.last_name}`,
        icon: "user",
        disabled: false,
      };
    },
    async save() {
      const teamIds = this.selectedOptions
        .filter(({ type }) => type === "team")
        .map(({ id }) => id);

      const userIds = this.selectedOptions
        .filter(({ type }) => type === "user")
        .map(({ id }) => id);

      if (userIds.length || teamIds.length) {
        this.$track("Created Alert");
        this.$q.loading.show({ delay: 0 });

        try {
          await this.$streemApiV1.post("alerts", {
            params: {
              target_id: this.archivedContentContext
                ? this.mention.tveyes_media_request.id
                : this.mention.id,
              target_type: this.archivedContentContext
                ? "media_request"
                : this.mentionType,
              keyword_matched: this.getKeywordMatched(),
              users_ids: userIds,
              teams_ids: teamIds,
              ...(this.text.length ? { text: this.text } : {}),
              options: {
                product: this.currentRouteProductMenu,
              },
            },
          });
        } finally {
          this.$q.loading.hide();
        }
      }

      this.$alert(this.$t("modals.push_to_team_modal.alert_message"));
      this.$modals.close("PushToTeamModal");
    },
    getKeywordMatched() {
      if (this.archivedContentContext) {
        return null; // we don't have keywords when dealing with archived content
      }

      if (this.mention.excerpts && this.mention.excerpts[0]) {
        return this.mention.excerpts[0].keywords[0];
      }

      return this.getMentionText.replace(/ .*/, "");
    },
    filterDropdownOptions(val) {
      this.teamUserFilter = val;
    },
    selectOption(option) {
      if (!option.disabled) {
        this.selectedOptions.push(option);
        this.selectedOption.disabled = true;
      }

      this.selectedOption = null;
    },
    deleteOption(option) {
      this.selectedOptions.splice(this.selectedOptions.indexOf(option), 1);
      Object.assign(option, { disabled: false });
    },
  },
};
</script>

<style lang="scss" scoped>
.team-list {
  max-height: 240px;
  border: 1px solid var(--s-color-denim-3);
  border-radius: var(--s-border-radius-md);
  overflow: auto;

  &-item {
    display: flex;
    gap: var(--s-spacing-md);
    padding-block: var(--s-spacing-md);
    padding-inline: var(--s-spacing-lg);
    align-items: center;
    cursor: pointer;

    &:hover {
      background-color: var(--s-color-denim-1);
    }

    &-avatar {
      background-color: var(--s-color-denim-3);
      border-radius: 50%;
      width: 30px;
      height: 30px;
      overflow: hidden;
      display: flex;
      align-items: center;
      justify-content: center;

      img {
        width: inherit;
        height: inherit;
      }
    }

    &-label {
      flex: 1;
    }

    &-team {
      font-size: 12px;
      color: var(--s-color-denim-7);

      .s-icon {
        font-size: 1em;
      }
    }
  }
}
</style>
